import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import BriefingCard from "@/components/BriefingCard";
import {
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  Minus,
  Info,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  Target,
  RefreshCw,
} from "lucide-react";

function fmtMoney(v, currency, opts = {}) {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
      ...opts,
    }).format(v);
  } catch {
    return `${currency || ""} ${Math.round(v).toLocaleString()}`;
  }
}

const CHANGE_META = {
  ADDED: { label: "Added", color: "text-emerald-700 bg-emerald-50 ring-emerald-200", Icon: Plus },
  WON: { label: "Won", color: "text-emerald-700 bg-emerald-50 ring-emerald-200", Icon: ArrowUpRight },
  GREW: { label: "Grew", color: "text-emerald-700 bg-emerald-50 ring-emerald-200", Icon: TrendingUp },
  SHRUNK: { label: "Shrunk", color: "text-amber-700 bg-amber-50 ring-amber-200", Icon: TrendingDown },
  SLIPPED: { label: "Slipped", color: "text-amber-700 bg-amber-50 ring-amber-200", Icon: ArrowDownRight },
  LOST: { label: "Lost", color: "text-red-700 bg-red-50 ring-red-200", Icon: Minus },
};

const SOURCE_LABEL = {
  default_too_few_closed: "too few closed deals overall",
  default_no_history: "no historical deals reached this stage",
  default_low_sample: "too few historical deals in this stage",
  default_degenerate_zero: "historical rate was 0% — likely CRM import artifact",
};

export default function ForecastPage() {
  const [data, setData] = useState(null);
  const [account, setAccount] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [err, setErr] = useState("");
  const [showWhy, setShowWhy] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setErr("");
    try {
      const [{ data: m }, { data: me }, { data: br }] = await Promise.all([
        api.get("/metrics/latest"),
        api.get("/auth/me"),
        api.get("/briefings/latest").catch(() => ({ data: { briefing: null } })),
      ]);
      setData(m);
      setAccount(me.account);
      setBriefing(br.briefing);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const recompute = async () => {
    setRecomputing(true);
    try {
      await api.post("/metrics/recompute");
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRecomputing(false);
    }
  };

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10 text-sm text-slate-500" data-testid="forecast-loading">
        Loading forecast…
      </div>
    );
  }

  if (data.empty) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10" data-testid="forecast-empty-state">
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center flex flex-col items-center">
          <TrendingUp className="h-10 w-10 text-slate-400" />
          <h2 className="mt-4 text-xl font-semibold text-slate-900">No forecast yet</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-md">
            Metrics are computed after the first sync + stage mapping are complete.
            Connect HubSpot, then confirm your stage mapping to generate a forecast.
          </p>
          <div className="mt-6 flex gap-3">
            <button
              onClick={() => nav("/dashboard/connect")}
              data-testid="forecast-connect-cta"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
            >
              Set up now
            </button>
            <button
              onClick={recompute}
              disabled={recomputing}
              data-testid="forecast-recompute-cta"
              className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${recomputing ? "animate-spin" : ""}`} />
              Recompute
            </button>
          </div>
        </div>
      </div>
    );
  }

  const m = data.metrics;
  const cur = m.currency || "USD";
  const weighted = m.forecast.weighted;
  const bookings_qtd = m.bookings.quarter_to_date;
  const projected = (bookings_qtd || 0) + (weighted || 0);
  const target = m.target.quarterly_target;
  // Gap to target = projected − target. Positive = tracking above goal.
  const gapSigned = target == null ? null : projected - target;
  const gapAhead = gapSigned != null && gapSigned >= 0;
  // Week-over-week delta of the PROJECTED total. Backend stores prev_weighted;
  // bookings QTD change WoW isn't stored on the metrics doc, so this equals the
  // change in the projected total under the same "bookings unchanged" fallback
  // the backend uses when there's no metrics_daily history yet.
  const prevWeighted = m.forecast?.prev_weighted;
  const projectedDelta = prevWeighted != null ? Math.round(weighted - prevWeighted) : null;
  const health = m.data_health_score;
  const showHealthBanner = health != null && health < 90;

  // Range bar percentages
  const best = m.forecast.best_case;
  const worst = m.forecast.worst_case;
  const rangeMax = Math.max(best, weighted, 1);
  const worstPct = Math.min(100, (worst / rangeMax) * 100);
  const likelyPct = Math.min(100, (weighted / rangeMax) * 100);

  return (
    <div className="max-w-6xl mx-auto w-full p-6 lg:p-10 space-y-8" data-testid="forecast-page">
      <header className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Forecast</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
            Current quarter
          </h1>
          <p className="mt-3 text-slate-500 max-w-xl">
            {m.forecast.quarter_start} → {m.forecast.quarter_end} · snapshot {m.metric_date} ·{" "}
            currency {cur}
            {m.excluded_currencies?.length ? ` (excluded: ${m.excluded_currencies.join(", ")})` : ""}
          </p>
        </div>
        <button
          onClick={recompute}
          disabled={recomputing}
          data-testid="forecast-recompute-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${recomputing ? "animate-spin" : ""}`} />
          Recompute
        </button>
      </header>

      {showHealthBanner && (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          data-testid="forecast-health-banner"
        >
          <span className="font-semibold">Heads up:</span> based on{" "}
          <span className="font-semibold">{health}%</span> of pipeline value with complete data. Fix
          the missing amounts / close dates in Data Health to sharpen this number.
        </div>
      )}

      {/* This week's briefing */}
      {briefing && briefing.briefing_json && (
        <BriefingCard briefing={briefing} cur={cur} bookings_qtd={bookings_qtd} />
      )}

      {/* Hero — projected quarter with WoW delta + supporting metrics */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8" data-testid="forecast-hero">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
          Projected close (this quarter)
        </div>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <div className="text-4xl sm:text-5xl font-extrabold tracking-tighter text-slate-900 number-tabular" data-testid="hero-projected">
            {fmtMoney(projected, cur)}
          </div>
          {projectedDelta != null && projectedDelta !== 0 && (
            <span
              className={`inline-block px-2.5 py-1 rounded-full text-white text-xs font-bold ${projectedDelta > 0 ? "bg-emerald-500" : "bg-red-600"}`}
              data-testid="hero-projected-delta"
            >
              {projectedDelta > 0 ? "↑" : "↓"} {fmtMoney(Math.abs(projectedDelta), cur)}
            </span>
          )}
          {projectedDelta === 0 && (
            <span className="inline-block px-2.5 py-1 rounded-full bg-slate-500 text-white text-xs font-bold" data-testid="hero-projected-delta">flat</span>
          )}
        </div>
        <div className="mt-2 text-xs text-slate-500">Bookings QTD + weighted forecast of open deals</div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-slate-200 pt-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Bookings QTD</div>
            <div className="mt-1 text-xl font-bold text-slate-900 number-tabular" data-testid="hero-bookings">
              {fmtMoney(bookings_qtd, cur)}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              vs previous quarter {fmtMoney(m.bookings.prev_quarter, cur)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Weighted forecast</div>
            <div className="mt-1 text-xl font-bold text-slate-900 number-tabular" data-testid="hero-weighted">
              {fmtMoney(weighted, cur)}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {m.forecast.open_deals_in_quarter} open deals in quarter
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Target</div>
            <div className="mt-1 text-xl font-bold text-slate-900 number-tabular" data-testid="hero-target">
              {target == null ? "—" : fmtMoney(target, cur)}
            </div>
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <Target className="h-3 w-3" />
              {target == null ? (
                <button
                  onClick={() => nav("/dashboard/settings")}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="hero-set-target-link"
                >
                  Set a quarterly target →
                </button>
              ) : (
                <>Quarterly goal</>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Gap to target</div>
            <div
              className={`mt-1 text-xl font-bold number-tabular ${
                target == null ? "text-slate-400" : gapAhead ? "text-emerald-600" : "text-red-600"
              }`}
              data-testid="hero-gap"
            >
              {target == null
                ? "—"
                : `${gapSigned > 0 ? "+" : gapSigned < 0 ? "−" : ""}${fmtMoney(Math.abs(gapSigned), cur)}`}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {target == null ? "Set a target to see the gap" : gapAhead ? "Tracking above goal" : "Tracking below goal"}
            </div>
          </div>
        </div>

        {/* Range bar */}
        <div className="mt-8" data-testid="forecast-range-bar">
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-slate-500">
            <span>Worst</span>
            <span>Likely (weighted)</span>
            <span>Best</span>
          </div>
          <div className="mt-2 relative h-2 rounded-full bg-slate-100">
            <div
              className="absolute left-0 top-0 h-2 rounded-full bg-amber-400"
              style={{ width: `${worstPct}%` }}
            />
            <div
              className="absolute left-0 top-0 h-2 rounded-full bg-slate-900 opacity-80"
              style={{ width: `${likelyPct}%` }}
            />
          </div>
          <div className="mt-2 grid grid-cols-3 text-xs number-tabular">
            <div className="text-amber-700 font-semibold">{fmtMoney(worst, cur)}</div>
            <div className="text-center text-slate-900 font-semibold">{fmtMoney(weighted, cur)}</div>
            <div className="text-right text-emerald-700 font-semibold">{fmtMoney(best, cur)}</div>
          </div>
        </div>

        <button
          onClick={() => setShowWhy((s) => !s)}
          className="mt-6 inline-flex items-center gap-1 text-sm text-slate-700 hover:text-slate-900"
          data-testid="forecast-why-toggle"
        >
          <Info className="h-4 w-4" />
          Why this number?
          {showWhy ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {showWhy && (
          <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-5" data-testid="forecast-why-details">
            <div className="text-xs text-slate-500 uppercase tracking-[0.18em] font-semibold">
              Stage win rates used
              {m.using_default_rates && (
                <span className="ml-2 normal-case tracking-normal text-amber-700">
                  (some or all stages using defaults)
                </span>
              )}
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(m.stage_win_rates).map(([stage, rate]) => {
                const src = (m.stage_rate_sources || {})[stage] || "computed";
                const isDefault = src !== "computed";
                const reason = SOURCE_LABEL[src] || src;
                return (
                  <div
                    key={stage}
                    className={`rounded-lg border p-3 ${
                      isDefault ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"
                    }`}
                    data-testid={`why-stage-${stage}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                        {stage}
                      </div>
                      <span
                        className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                          isDefault ? "bg-amber-200 text-amber-900" : "bg-emerald-100 text-emerald-800"
                        }`}
                        data-testid={`why-stage-${stage}-source`}
                      >
                        {isDefault ? "default" : "computed"}
                      </span>
                    </div>
                    <div className="mt-1 text-xl font-extrabold text-slate-900 number-tabular">
                      {Math.round(rate * 100)}%
                    </div>
                    {isDefault && (
                      <div className="text-[10px] text-amber-800 mt-1">{reason}</div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 text-xs text-slate-500 leading-relaxed">
              For each open deal with a close date this quarter, we multiply its amount by the win
              rate of its current stage. Best case sums all Proposal + Negotiation amounts (100%
              hypothetical). Worst case sums only Negotiation × their win rate.
              {m.closed_deals_count_12mo != null && (
                <>
                  {" "}<span className="text-slate-600">Sample:</span> {m.closed_deals_count_12mo} closed
                  deals in the last 12 months.
                </>
              )}
            </div>
          </div>
        )}
      </section>

      {/* What changed this week */}
      <section data-testid="changes-section">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h2 className="text-xl font-semibold text-slate-900">What changed this week</h2>
          <div className="text-xs text-slate-500" data-testid="changes-reference-label">
            {m.changes_vs_7d.reference_date ? (
              <>
                vs snapshot from{" "}
                <span className="font-semibold text-slate-700">
                  {m.changes_vs_7d.reference_days_ago} {m.changes_vs_7d.reference_days_ago === 1 ? "day" : "days"} ago
                </span>{" "}
                <span className="text-slate-400">({m.changes_vs_7d.reference_date})</span>
                {m.changes_vs_7d.is_fallback && (
                  <span className="ml-1 text-amber-700">— history building up</span>
                )}
              </>
            ) : (
              "vs snapshot —"
            )}
          </div>
        </div>
        {(!m.changes_vs_7d.changes || m.changes_vs_7d.changes.length === 0) ? (
          <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="changes-empty">
            {m.changes_vs_7d.reference_date
              ? "No deal-level changes since that snapshot."
              : "Only one snapshot so far — comparisons appear once your second snapshot lands."}
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-hidden">
            {m.changes_vs_7d.changes.slice(0, 50).map((c, i) => {
              const meta = CHANGE_META[c.type] || CHANGE_META.SLIPPED;
              const Icon = meta.Icon;
              return (
                <div
                  key={`${c.deal_id}-${i}`}
                  className="flex items-center gap-4 px-5 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors"
                  data-testid={`change-row-${c.deal_id}`}
                >
                  <span
                    className={`inline-flex items-center gap-1 rounded-full ring-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${meta.color}`}
                  >
                    <Icon className="h-3 w-3" />
                    {meta.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {c.deal_name || "Untitled deal"}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{c.details}</div>
                  </div>
                  <div className="text-sm text-slate-900 font-semibold number-tabular">
                    {fmtMoney(c.amount, cur)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Small footer with monthly stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="forecast-mini-stats">
        <MiniStat label="Bookings MTD" value={fmtMoney(m.bookings.month_to_date, cur)} sub={`vs prev month ${fmtMoney(m.bookings.prev_month, cur)}`} />
        <MiniStat label="Data health" value={health != null ? `${health}%` : "—"} sub="of open pipeline value complete" />
        <MiniStat label="Excluded deals" value={m.excluded_deal_count} sub="other currencies" />
        <MiniStat label="At risk" value={m.at_risk_deals?.length ?? 0} sub={<button onClick={() => nav("/dashboard/risk")} className="text-blue-600 hover:text-blue-700 font-medium">see risk radar →</button>} />
      </section>

      {err && (
        <div className="text-sm text-red-600" data-testid="forecast-error">
          {err}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-extrabold text-slate-900 number-tabular">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
}

